import React, { useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useWindowSize } from 'react-use'
import { AccountContext } from '@/contexts/account'
import {
  listContainerStyle,
  listContentsWrapperStyle,
  listContentsStyle,
  HEADER_HEIGHT,
  MAIN_CONTAINER_WITH_HEADER_BAR_PADDING_VERTICAL,
  CONTAINER_WITH_HEADER_BUTTON_LIST_MARGIN,
  TAB_HEIGHT,
  GRAY_LABEL_HEIGHT,
  LIST_FOOTER_HEIGHT,
} from '@/constants/layout'
import {
  settingHeaderContainerStyle,
  listWrapperStyle,
  listStyle,
  hideListStyle,
  deleteStyle,
} from '@/components/pages/setting/guest-room/room/layout'
import { getAdminGuestRoomType, deleteAdminGuestRoomType } from '@/apis/aipass'
import { GuestRoomTab } from '@/components/pages/setting/guest-room/tab'
import { RoomTypeModal } from '@/components/pages/setting/guest-room/room/_room/modal/room-type-modal'
import { SettingGuestRoomLabel } from '@/components/pages/setting/guest-room/room/_room/label'
import { SettingGuestRoomEditButtons } from '@/components/pages/setting/guest-room/room/_room/edit-buttons'
import { EditFooter } from '@/components/organisms/edit-footer'
import { GuestRoomTypeType } from '@/models/guest-room-type'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { SettingsLayout } from '@/components/layouts/settings-layout'

export const SettingGuestRoomType: React.FC = () => {
  const windows = useWindowSize()
  const { errorHandler } = useErrorHandler()
  const { t, i18n } = useTranslation()
  const { account } = useContext<any>(AccountContext)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isUpload, setIsUpload] = useState<boolean>(false)
  const [showFooter, setShowFooter] = useState<boolean>(false)
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false)
  const [showEditModal, setShowEditModal] = useState<boolean>(false)
  const [deleteRoomIds, setDeleteRoomIds] = useState<string[]>([])
  const [editRoom, setEditRoom] = useState<GuestRoomTypeType>()
  const [guestRoomTypes, setGuestRoomTypes] = useState<GuestRoomTypeType[]>([])

  useEffect(() => {
    if (account && account?.hotel) {
      _fetchGuestRoomType()
    }
  }, [account])

  useEffect(() => {
    if (isUpload && account) {
      _fetchGuestRoomType()
    }
  }, [isUpload])

  const _fetchGuestRoomType = async () => {
    setIsLoading(true)
    await getAdminGuestRoomType().then(res => {
      setGuestRoomTypes(res)
      setIsLoading(false)
      setIsUpload(false)
    })
  }

  const updateGuestRoomType = async (value: GuestRoomTypeType) => {
    setEditRoom(value)
    setShowEditModal(true)
  }

  const onCLickDelete = (id: string) => {
    setShowFooter(true)
    setDeleteRoomIds(deleteRoomIds => [...deleteRoomIds, id])
  }

  const onClickFooterSaveButton = async () => {
    try {
      setShowFooter(false)
      if (!deleteRoomIds) {
        return
      }
      if (window.confirm(t('Are you sure you want to delete it'))) {
        await deleteAdminGuestRoomType(deleteRoomIds)
        setDeleteRoomIds([])
      } else {
        setDeleteRoomIds([])
      }
    } catch (error) {
      errorHandler(error)
    }
  }

  const onClickFooterCancelButton = () => {
    setShowFooter(false)
    setDeleteRoomIds([])
  }

  const onClickModalCancelButton = () => {
    setShowCreateModal(false)
    setShowEditModal(false)
  }
  const onClickModalSaveButton = async () => {
    setShowCreateModal(false)
    setShowEditModal(false)
    await _fetchGuestRoomType()
  }

  const listItems = [
    { label: t('Display order'), width: 62 },
    { label: t('Room type ID'), width: 112 },
    { label: t('Room type name'), width: 227 },
    { label: t('Room type short name'), width: 148 },
    { label: t('Number of rooms'), width: 79 },
    { label: t('Room type code'), width: 470 },
  ]

  const onClickAddButton = () => {
    setShowCreateModal(true)
  }

  const computedNextDisplayOrder = () => {
    return guestRoomTypes?.length ? guestRoomTypes[guestRoomTypes.length - 1].displayOrder + 1 : 1
  }

  const footerContent = showFooter ? <EditFooter onSave={onClickFooterSaveButton} onCancel={onClickFooterCancelButton} /> : undefined

  return (
    <>
      {showCreateModal && (
        <RoomTypeModal
          title={t('Add room type')}
          onClickCancel={onClickModalCancelButton}
          onClickSave={onClickModalSaveButton}
          nextDisplayOrder={computedNextDisplayOrder()}
          setIsLoading={setIsLoading}
        />
      )}
      {showEditModal && (
        <RoomTypeModal
          title={t('Edit Room Type')}
          onClickCancel={onClickModalCancelButton}
          onClickSave={onClickModalSaveButton}
          editRoom={editRoom}
          setIsLoading={setIsLoading}
        />
      )}
      <SettingsLayout loading={isLoading} footerContent={footerContent}>
        <>
          <div css={settingHeaderContainerStyle}>
            <GuestRoomTab currentTab="roomType" />
          </div>
          <div css={[listContainerStyle]}>
            <SettingGuestRoomEditButtons onClickAddButton={onClickAddButton} />
            <SettingGuestRoomLabel listItems={listItems} />
            <div
              css={[listContentsWrapperStyle]}
              style={{
                maxHeight:
                  windows.height -
                  HEADER_HEIGHT -
                  MAIN_CONTAINER_WITH_HEADER_BAR_PADDING_VERTICAL -
                  CONTAINER_WITH_HEADER_BUTTON_LIST_MARGIN -
                  TAB_HEIGHT -
                  GRAY_LABEL_HEIGHT -
                  (showFooter ? LIST_FOOTER_HEIGHT : 0),
              }}
            >
              <div css={listContentsStyle}>
                {guestRoomTypes &&
                  guestRoomTypes.map((value: GuestRoomTypeType, index) => {
                    if (deleteRoomIds.includes(value.id)) {
                      return <div key={index} css={hideListStyle} />
                    } else {
                      return (
                        <div key={index} css={listWrapperStyle}>
                          <div css={listStyle} onClick={() => updateGuestRoomType(value)}>
                            <div style={{ width: listItems[0].width }}>{value.displayOrder}</div>
                            <div style={{ width: listItems[1].width }}>{value.roomTypeId}</div>
                            <div style={{ width: listItems[2].width }}>{i18n.language === 'ja' ? value.typeNameJa : value.typeNameEn}</div>
                            <div style={{ width: listItems[3].width }}>{value.typeShortName}</div>
                            <div style={{ width: listItems[4].width }}>{value.quantity || value.quantity == 0 ? value.quantity : '-'}</div>
                            <div style={{ width: listItems[5].width }}>
                              {value.otaCodes ? value.otaCodes.map(({ code }) => code).join(', ') : ''}
                            </div>
                          </div>
                          <img
                            css={deleteStyle}
                            src={require('@/static/images/room_delete_gold.svg')}
                            onClick={() => onCLickDelete(value.id)}
                          />
                        </div>
                      )
                    }
                  })}
              </div>
            </div>
          </div>
        </>
      </SettingsLayout>
    </>
  )
}
